import { Outlet, useLocation, redirect, createFileRoute } from "@tanstack/react-router";
import { useRef, useEffect } from "react";
import NavigationTop from "@/components/general/Navigation/NavigationTop";
import NavigationSide from "@/components/general/Navigation/NavigationSide";
import NavigationBottom from "@/components/general/Navigation/NavigationBottom";
import Footer from "@/components/general/common/Footer";
import { Toaster } from "@/components/shadcn/sonner";
import { PageNotFound } from "@/pages/notFound/page";
import { RouteUrl } from "@/configs/router/routes";
import { SelectedMatchProvider, useSelectedMatch } from "@/context/matchContext";
import { SelectedTournamentProvider } from "@/context/tournamentContext";
import { SelectedTeamProvider, useSelectedTeam } from "@/context/teamContext";
import { OnboardingProvider } from "@/components/general/Onboarding";

function TeamChangeListener() {
    const { teamId } = useSelectedTeam();
    const { setMatchIds, setTournamentName } = useSelectedMatch();
    const location = useLocation();
    const pendingUrlMatchIdRef = useRef<number | null>(
        (() => {
            const id = new URLSearchParams(location.search).get("matchId");
            return id ? parseInt(id) : null;
        })()
    );
    const isMountRef = useRef(true);

    useEffect(() => {
        if (isMountRef.current) {
            isMountRef.current = false;
            return;
        }

        const pending = pendingUrlMatchIdRef.current;
        pendingUrlMatchIdRef.current = null;

        if (pending !== null) {
            setMatchIds([pending]);
        } else {
            setMatchIds([]);
        }
        setTournamentName(null);
    }, [teamId, setMatchIds, setTournamentName]);

    return null;
}

const TENANT_STORAGE_KEY = "hockeyly_selected_tenant";
const AUTH_METHOD_KEY = "hockeyly_auth_method";
const TOKEN_STORAGE_KEY = "token";

export const Route = createFileRoute("/_pathlessLayout")({
    component: LayoutComponent,
    notFoundComponent: () => <PageNotFound />,
    beforeLoad: ({ location }) => {
        const storedTenant = localStorage.getItem(TENANT_STORAGE_KEY);
        const authMethod = localStorage.getItem(AUTH_METHOD_KEY);
        const token = localStorage.getItem(TOKEN_STORAGE_KEY);

        const isAuthenticated = !!storedTenant || (authMethod === "token" && !!token);
        if (!isAuthenticated) {
            const redirectTo = location.pathname + location.searchStr;
            if (redirectTo && redirectTo !== RouteUrl.Login) {
                sessionStorage.setItem("hockeyly_post_login_redirect", redirectTo);
            }
            throw redirect({ to: RouteUrl.Login, replace: true });
        }
    },
});

function LayoutComponent() {
    const scrollRef = useRef<HTMLDivElement>(null);
    const location = useLocation();

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
        }
    }, [location.pathname]);

    return (
        <SelectedMatchProvider>
            <SelectedTournamentProvider>
                <SelectedTeamProvider>
                    <OnboardingProvider>
                        <TeamChangeListener />
                        <div className="h-screen flex flex-col overflow-hidden bg-surface pt-16">
                            <NavigationTop />
                            <div className="flex flex-1 min-h-0">
                                <NavigationSide />
                                <div
                                    ref={scrollRef}
                                    className="flex-1 min-h-0 overflow-auto pb-16 lg:pb-0 flex flex-col"
                                >
                                    <main className="flex-1 min-h-full flex flex-col">
                                        <Outlet />
                                    </main>
                                </div>
                            </div>
                            <div className="hidden lg:block flex-shrink-0">
                                <Footer />
                            </div>
                            <NavigationBottom />
                            <Toaster />
                        </div>
                    </OnboardingProvider>
                </SelectedTeamProvider>
            </SelectedTournamentProvider>
        </SelectedMatchProvider>
    );
}
